/**
 * FrontendLogger
 *
 * Structured logger for browser environments. Outputs JSON log entries
 * through configurable transports (console for dev, batch HTTP for prod).
 *
 * Replaces the old Logger singleton with structured, shippable logs.
 */

import type {
  FrontendLogLevel,
  FrontendLogEntry,
  FrontendLoggerConfig,
  LogTransport,
  JsonObject,
} from './types';
import { ConsoleTransport } from './transports/console-transport';
import { BatchTransport } from './transports/batch-transport';
import { getSessionId, getFlowCorrelationId } from './correlation/session-correlation';

const LEVEL_PRIORITY: Record<FrontendLogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

export class FrontendLogger {
  private readonly service: string;
  private readonly storeId: string;
  private readonly environment: string;
  private readonly minLevel: number;
  private readonly transports: LogTransport[];
  private readonly bindings: JsonObject;

  constructor(config: FrontendLoggerConfig, bindings?: JsonObject) {
    this.service = config.service;
    this.storeId = config.storeId ?? 'unknown';
    this.environment = config.environment ?? (typeof process !== 'undefined' ? (process.env.NODE_ENV ?? 'development') : 'development');
    this.minLevel = LEVEL_PRIORITY[config.level ?? 'info'];
    this.bindings = bindings ?? {};

    this.transports = [];

    // Always add console in development
    if (this.environment === 'development' || config.level === 'debug') {
      this.transports.push(new ConsoleTransport());
    }

    // Add batch transport if shipping is enabled
    if (config.shippingEnabled && config.shippingEndpoint) {
      this.transports.push(
        new BatchTransport({
          endpoint: config.shippingEndpoint,
          batchSize: config.batchSize,
          flushIntervalMs: config.flushIntervalMs,
        }),
      );
    }

    // If no transports configured, default to console
    if (this.transports.length === 0) {
      this.transports.push(new ConsoleTransport());
    }
  }

  debug(message: string, context?: JsonObject): void {
    this.log('debug', message, context);
  }

  info(message: string, context?: JsonObject): void {
    this.log('info', message, context);
  }

  warn(message: string, context?: JsonObject): void {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error, context?: JsonObject): void {
    const errorContext = error
      ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
        }
      : undefined;

    const entry = this.buildEntry('error', message, context);
    if (errorContext) {
      entry.error = errorContext;
    }

    for (const transport of this.transports) {
      transport.write(entry);
    }
  }

  /**
   * Creates a child logger with additional bound context.
   */
  child(additionalBindings: JsonObject): FrontendLogger {
    const merged: JsonObject = { ...this.bindings, ...additionalBindings };
    const childLogger = new FrontendLogger(
      {
        service: this.service,
        storeId: this.storeId,
        environment: this.environment,
        level: this.getLevelName(),
      },
      merged,
    );
    // Share transports with parent
    childLogger.transports.length = 0;
    for (const t of this.transports) {
      childLogger.transports.push(t);
    }
    return childLogger;
  }

  /**
   * Flushes all transports (call before page unload or route change).
   */
  async flush(): Promise<void> {
    await Promise.all(this.transports.map((t) => t.flush()));
  }

  private log(level: FrontendLogLevel, message: string, context?: JsonObject): void {
    if (LEVEL_PRIORITY[level] < this.minLevel) {
      return;
    }

    const entry = this.buildEntry(level, message, context);

    for (const transport of this.transports) {
      transport.write(entry);
    }
  }

  private buildEntry(level: FrontendLogLevel, message: string, context?: JsonObject): FrontendLogEntry {
    const mergedContext: JsonObject | undefined =
      Object.keys(this.bindings).length > 0 || context
        ? { ...this.bindings, ...context }
        : undefined;

    return {
      timestamp: new Date().toISOString(),
      level: level.toUpperCase(),
      service: this.service,
      environment: this.environment,
      correlationId: getFlowCorrelationId(),
      sessionId: getSessionId(),
      storeId: this.storeId,
      message,
      context: mergedContext,
      page: this.getPageContext(),
    };
  }

  private getPageContext(): FrontendLogEntry['page'] {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return {
      path: window.location.pathname,
      title: document.title || undefined,
      referrer: document.referrer || undefined,
    };
  }

  private getLevelName(): FrontendLogLevel {
    const entries = Object.entries(LEVEL_PRIORITY) as [FrontendLogLevel, number][];
    const match = entries.find(([, priority]) => priority === this.minLevel);
    return match ? match[0] : 'info';
  }
}

/**
 * Creates a configured FrontendLogger from environment variables.
 *
 * Reads: VITE_LOG_LEVEL, VITE_LOG_SHIPPING_ENABLED, VITE_LOG_ENDPOINT,
 *        NEXT_PUBLIC_STORE, NODE_ENV
 */
export function createFrontendLogger(serviceName: string): FrontendLogger {
  const getEnv = (key: string): string | undefined => {
    if (typeof process !== 'undefined' && process.env) {
      return process.env[key] as string | undefined;
    }
    return undefined;
  };

  return new FrontendLogger({
    service: serviceName,
    storeId: getEnv('NEXT_PUBLIC_STORE') ?? getEnv('VITE_STORE_ID'),
    environment: getEnv('NODE_ENV') ?? 'development',
    level: (getEnv('VITE_LOG_LEVEL') ?? getEnv('NEXT_PUBLIC_LOG_LEVEL') ?? 'warn') as FrontendLogLevel,
    shippingEnabled: getEnv('VITE_LOG_SHIPPING_ENABLED') === 'true',
    shippingEndpoint: getEnv('VITE_LOG_ENDPOINT'),
    batchSize: 50,
    flushIntervalMs: 5000,
  });
}
